import React from 'react';
import { Helmet } from 'react-helmet-async';
import { siteSeo, buildAbsoluteUrl } from '../../config/seo';

/**
 * Seo component
 * - Sets title, description, canonical
 * - Sets Open Graph and Twitter meta
 * - Optionally injects JSON-LD structured data
 */
const Seo = ({
  title,
  description,
  image,
  canonicalPath,
  noIndex = false,
  keywords, // string or string[]; if array, extends defaults
  jsonLd, // object or array of objects
}) => {
  const resolvedTitle = title ? `${title} · ${siteSeo.siteName}` : siteSeo.defaultTitle;
  const resolvedDescription = description || siteSeo.defaultDescription;
  const resolvedImage = image || siteSeo.defaultImage;
  const canonicalUrl = buildAbsoluteUrl(canonicalPath || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/'));
  const absoluteImage = buildAbsoluteUrl(resolvedImage);

  // Resolve keywords: use defaults; if a page provides an array, merge with defaults; if string, override
  let resolvedKeywords;
  if (Array.isArray(keywords)) {
    const base = Array.isArray(siteSeo.defaultKeywords) ? siteSeo.defaultKeywords : [];
    const merged = [...base, ...keywords].map(k => (k || '').trim()).filter(Boolean);
    // de-duplicate while preserving order
    const seen = new Set();
    resolvedKeywords = merged.filter(k => (seen.has(k) ? false : (seen.add(k), true)));
  } else if (typeof keywords === 'string') {
    resolvedKeywords = keywords;
  } else {
    resolvedKeywords = siteSeo.defaultKeywords;
  }

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {resolvedKeywords && (
        <meta
          name="keywords"
          content={Array.isArray(resolvedKeywords) ? resolvedKeywords.join(', ') : resolvedKeywords}
        />
      )}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteSeo.siteName} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={absoluteImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={siteSeo.twitterHandle} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* JSON-LD */}
      {jsonLdArray.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;


